import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { RootState } from '@root/rootReducer';
import HistoryDropdown from '@components/HistoryDropdown';
import { useLocation, useNavigate } from 'react-router-dom';
import { RecentStudiesDropdownState } from 'config/browserConfig';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { useBufferPool } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import styles from './TopMenu.module.scss';

function parseStudyTime(studyTime: string | null): string {
  if (!studyTime) return "00:00:00";
  const clean = studyTime.split(".")[0].padStart(6, "0");
  const hh = clean.slice(0, 2);
  const mm = clean.slice(2, 4);
  const ss = clean.slice(4, 6);
  return `${hh}:${mm}:${ss}`;
}

type RecentStudiesDropdownProps = {
  disabled?:boolean;
}
const RecentStudiesDropdown: React.FC<RecentStudiesDropdownProps>= (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {clearPool} = useBufferPool();
const {
    selectedLayoutConfig,
  } = useAnalysisPage();
  const {
    updateSelectedStudy
  } = useBrowserPage();
const {updateSelectedPanelConfig,
  } = usePanelContainer();
  const { recentStudies, selectedStudyId } = useSelector(
    (state: RootState) => ({
      recentStudies: state.study.recentStudies,
      selectedStudyId: state.study.selectedStudyId,
    }),
    shallowEqual,
  );

  // 드롭다운의 상태
  // 없으면 empty
  // /analysis 페이지 아니면 return
  // 나머진 'active'
  const dropdownState: RecentStudiesDropdownState = useMemo(() => {
    if (recentStudies.length === 0) return 'empty';
    if (location.pathname.startsWith('/browser')) return 'return';
    return 'active';
  }, [recentStudies, selectedStudyId, location.pathname]);

  // placeholder
  const placeholder = useMemo(() => {
    switch (dropdownState) {
      case 'empty':
        return 'No Recent Studies';
      case 'return':
        return 'Resume Study';
      case 'active':
      default:
        return 'Current Study';
    }
  }, [dropdownState]);

  // 드롭다운 wrapper
  // return면 드롭다운 클릭 시 바로 네비게이션
  const handleWrapperClick = useCallback(
    (e: React.MouseEvent) => {
      if (dropdownState === 'return') {
        e.preventDefault();
        e.stopPropagation();
        updateSelectedPanelConfig(selectedLayoutConfig.panelConfig.filter(p=>p.panelId==='DICOMPanel')[0]);
        navigate(`/analysis/${selectedStudyId}`);
      }
    },
    [dropdownState, dispatch, selectedStudyId],
  );

  // onChange
  // active면 선택한 스터디 로 업데이트
  const handleDropdownChange = useCallback(
    (value: string) => {
      if (dropdownState === 'active') {
        const study = recentStudies.find(study=>study.id===value)!;
        if(study.id !== selectedStudyId){
          clearPool();
          updateSelectedStudy(study);
        }
        navigate(`/analysis/${value}`);
      }
    },
    [dropdownState, dispatch, recentStudies],
  );

  // recentStudies 바뀌면 옵션 재구성
  const options = useMemo(() => {
    return recentStudies.map((study) => ({
      value: study.id,
      label: `${study.patient.patientId} - ${study.patient.patientAge} - ${study.studyDate} - ${parseStudyTime(study.studyTime)}`,
    }));
  }, [recentStudies]);
  return (

      // className={styles.historyContainer}
      <HistoryDropdown
        placeholder={placeholder}
        options={dropdownState == 'active' ? options : []}
        value={selectedStudyId || ''}
        disabled={dropdownState !== 'active'}
        onChange={handleDropdownChange}
        onClick={handleWrapperClick}
        {...props}
      />

  );
};

export default React.memo(RecentStudiesDropdown);
